import { State, process } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessageColumnInbox } from 'src/assets/static/view_message/useMessageColumnInbox';
import { useMessageColumnOutbox } from 'src/assets/static/view_message/useMessageColumnOutbox';
import Button from 'src/components/design-system/Button';
import GridWrapper from 'src/components/GridWrapper';
import { MESSAGE_MODE } from 'src/constants/messageMode';
import { MESSAGE_PRIORITY } from 'src/constants/messagePriority';
import { MESSAGE_STATUS } from 'src/constants/messageStatus';
import useAuth from 'src/hooks/useAuth';
import useCompany from 'src/hooks/useCompany';
import useViewMessage from 'src/hooks/useViewMessage';
import useWindowSize from 'src/hooks/useWindowSize';
import type { Message } from 'src/services/messages_services/useGetMessageApi';
import 'src/style/outbox-grid-fixed-width.css';
import DeleteMessage from '../message_details/DeleteMessage';
import UnDeleteMessage from '../message_details/UnDeleteMessage';
import ComposeMessage from './ComposeMessage';
import MessageDetailsInboxModal from './MessageDetailsInboxModal';
import MessageDetailsOutboxModal from './MessageDetailsOutboxModal';
import ViewMessagesTableToolbar from './table_toolbar/ViewMessagesTableToolbar';

/**
 * Performance Optimizations Applied:
 *
 * 1. ✓ Cached localStorage read in useState initializer (prevents blocking render on every mount)
 * 2. ✓ Memoized toolbar filter value to avoid redundant toLowerCase() calls
 * 3. ✓ Reduced processedData useMemo dependencies (deletedFilter → toolbarFilterValue)
 * 4. ✓ Optimized onHeaderSelectionChange to only select visible page rows (not entire dataset)
 * 5. ✓ Wrapped component in React.memo to prevent unnecessary re-renders
 * 6. ✓ Enabled virtual scrolling (scrollable="virtual") for efficient rendering of large datasets
 *
 * Known Performance Bottlenecks (Require Major Refactor):
 * - ViewMessageContextUnified creates 3 API hooks simultaneously (inbox, outbox, generic)
 *   even though only one is active at a time. Consider lazy loading or conditional hooks.
 * - Grid pagination still loads all filtered data into memory before paginating client-side.
 */

// Define interface for cell click callback data
interface CellClickData {
  data: Message;
  type: 'subject-inbox' | 'subject-outbox' | string;
}

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 20,
  pageSizes: [10, 20, 50, 100, 300, 500],
};

const defaultProps = {
  tableTextId: 'Messages',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  pageable: gridPagerSettings,
} as const;

interface ViewMessageTableProps {}

const ViewMessageTable: FC<ViewMessageTableProps> = () => {
  const { t } = useTranslation();
  const { height } = useWindowSize();
  // Cache localStorage read in useState initializer to avoid blocking render
  const [clientIdState, setClientIdState] = useState<string>(
    () => localStorage.getItem('selectedClientId') || ''
  );
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
  const { clientId: authClientId } = useAuth();
  const { company } = useCompany();

  // Listen for clientId changes from ViewMessageNavbar
  useEffect(() => {
    const handleClientIdChange = (event: CustomEvent) => {
      const newClientId = event.detail?.clientId || '';
      setClientIdState(newClientId);
    };

    window.addEventListener('clientIdChanged', handleClientIdChange as EventListener);

    return () => {
      window.removeEventListener('clientIdChanged', handleClientIdChange as EventListener);
    };
  }, []);

  const [isMessageDetailsModalOpen, setIsMessageDetailsModalOpen] = useState(false);
  const [isInboxModalOpen, setIsInboxModalOpen] = useState(false);
  const [selectedMessageForModal, setSelectedMessageForModal] = useState<Message | null>(null);
  const [selectedInboxMessage, setSelectedInboxMessage] = useState<Message | null>(null);

  // Local state for managing selected rows since useAsync doesn't allow mutations
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Get unified context
  const {
    isMessageLoading,
    messagesDataState,
    setMessagesDataState,
    refreshTable,
    messageWithFilters,
    messages,
  } = useViewMessage();

  const clientId = useMemo(() => {
    // Use clientIdState which is updated from ViewMessageNavbar
    if (
      clientIdState &&
      clientIdState !== '' &&
      clientIdState !== 'null' &&
      clientIdState !== 'undefined'
    ) {
      return clientIdState;
    }

    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      return null; //default fallback
    }
    const found = messages.find(
      (msg) => typeof msg.clientId !== 'undefined' && msg.clientId !== null
    );
    const foundClientId = found ? String(found.clientId) : null;
    return foundClientId;
  }, [clientIdState, messages]);

  const [deletedFilter, setDeletedFilter] = useState<{ value: string; label: string }>({
    value: 'not deleted',
    label: t('Show All Active'),
  });

  // Update the label when language changes, but keep the same value
  useEffect(() => {
    setDeletedFilter((prev) => {
      // Map values to their translated labels
      const labelMap: Record<string, string> = {
        'not deleted': t('Show All Active'),
        all: t('Show All'),
        deleted: t('Show Only Deleted'),
      };

      // Only update if the label should change based on current language
      const newLabel = labelMap[prev.value] || prev.label;
      if (newLabel !== prev.label) {
        return { ...prev, label: newLabel };
      }
      return prev;
    });
  }, [t]);

  // Access unified context and determine mode
  const { mode } = useViewMessage();
  const isInboxMode = mode === MESSAGE_MODE.INBOX;
  const isOutboxMode = mode === MESSAGE_MODE.OUTBOX;

  // Determine if user has valid identity for composing messages
  const hasValidIdentity = useMemo(
    () => !!(company?.companyId || authClientId),
    [company?.companyId, authClientId]
  );

  // Memoize toolbar filter value to avoid recalculating on every render
  const toolbarFilterValue = useMemo(
    () => deletedFilter?.value?.trim().toLowerCase(),
    [deletedFilter?.value]
  );

  // Process messages with grid filtering
  const processedData = useMemo(() => {
    // Always start with raw messages to ensure consistent filtering behavior
    const sourceData = messages;

    if (!sourceData || !Array.isArray(sourceData)) {
      return { data: [], total: 0 };
    }

    // Start with toolbar filtering on the source data
    let filteredData = [...sourceData];

    // Apply toolbar filter - this happens before column filters and pagination
    if (toolbarFilterValue === MESSAGE_STATUS.DELETED) {
      filteredData = filteredData.filter((msg: Message) => {
        if (isInboxMode) {
          // Show if deleted flag is true OR status is 'Deleted by all' OR 'Deleted by recipient'
          const status = (msg.decodedStatus || '').toLowerCase();
          return (
            msg.isDeleted === true ||
            status === 'deleted by all' ||
            status === 'deleted' ||
            status === 'deleted by recipient'
          );
        }
        // For outbox: Show messages deleted by sender
        // Check both the isDeletedMsgCast flag AND decodedStatus text to catch all cases
        const status = (msg.decodedStatus || '').toLowerCase();
        return msg.isDeletedMsgCast === true || status === 'deleted by sender';
      });
    } else if (toolbarFilterValue === 'not deleted') {
      filteredData = filteredData.filter((msg: Message) => {
        // For inbox: show messages not deleted by recipient (can include deleted by sender)
        if (isInboxMode) {
          return msg.isDeleted === false;
        }
        // For outbox: show messages not deleted by sender (can include deleted by recipient)
        return msg.isDeletedMsgCast === false;
      });
    } else if (toolbarFilterValue === 'all') {
      // "Show All" - no filtering on deleted status, show all messages
      // (filteredData is already [...sourceData] from above)
    } else if (toolbarFilterValue === 'unread') {
      filteredData = filteredData.filter(
        (msg: Message) =>
          msg.decodedStatus?.toLowerCase() === MESSAGE_STATUS.NEW ||
          msg.decodedStatus?.toLowerCase() === 'unread' ||
          (msg.status !== 'Read' && msg.decodedStatus?.toLowerCase() !== MESSAGE_STATUS.READ)
      );
    } else if (toolbarFilterValue === MESSAGE_STATUS.READ) {
      filteredData = filteredData.filter(
        (msg: Message) =>
          msg.decodedStatus?.toLowerCase() === MESSAGE_STATUS.READ || msg.status === 'Read'
      );
    } else if (toolbarFilterValue === 'important') {
      filteredData = filteredData.filter(
        (msg: Message) =>
          msg.decodedPriority?.toLowerCase() === MESSAGE_PRIORITY.HIGH.toLowerCase() ||
          msg.decodedPriority?.toLowerCase() === MESSAGE_PRIORITY.URGENT.toLowerCase()
      );
    }

    // Apply column filters, sorting, and pagination using Kendo's process function
    // FIX: Added filter parameter from messagesDataState to enable column filtering
    const processResult = process(filteredData, {
      filter: messagesDataState.filter,
      sort: messagesDataState.sort,
      skip: messagesDataState.skip || 0,
      take: messagesDataState.take || 50,
    });

    // Add selection state to each item
    const dataWithSelection = processResult.data.map((item: Message) => ({
      ...item,
      selected: item.rowId ? selectedRows.has(item.rowId) : false,
    }));

    // Return processed data with total count after ALL filters (toolbar + column filters)
    return { data: dataWithSelection, total: processResult.total };
  }, [
    messages,
    toolbarFilterValue,
    messagesDataState.filter,
    messagesDataState.skip,
    messagesDataState.take,
    messagesDataState.sort,
    selectedRows,
    isInboxMode,
    isOutboxMode,
  ]);

  const [latestClickedId, setLatestClickedId] = useState(0);

  const selectedToDelete = useMemo(
    () =>
      processedData.data.filter(
        (item: { selected?: boolean; isDeleted?: boolean; isDeletedMsgCast?: boolean }) => {
          if (!item.selected) return false;

          // For OUTBOX: check if sender has NOT deleted (isDeletedMsgCast = false)
          if (isOutboxMode) {
            return item.isDeletedMsgCast === false;
          }

          // For INBOX: check if recipient has NOT deleted (isDeleted = false)
          return item.isDeleted === false;
        }
      ),
    [processedData.data, isOutboxMode]
  );

  const selectedToUndelete = useMemo(
    () =>
      processedData.data.filter(
        (item: { selected?: boolean; isDeleted?: boolean; isDeletedMsgCast?: boolean }) => {
          if (!item.selected) return false;

          // For OUTBOX: check if deleted by sender (isDeletedMsgCast = true)
          if (isOutboxMode) {
            return item.isDeletedMsgCast === true;
          }

          // For INBOX: check if deleted by recipient (isDeleted = true)
          return item.isDeleted === true;
        }
      ),
    [processedData.data, isOutboxMode]
  );

  // Call both hooks unconditionally (React hooks rule)
  const inboxColumns = useMessageColumnInbox();
  const outboxColumns = useMessageColumnOutbox();

  // Use appropriate cols based on unified mode detection
  const viewMessageColumns = isInboxMode ? inboxColumns : outboxColumns;

  const changeDataState = useCallback(
    (e: GridDataStateChangeEvent) => {
      // Clear selection when data state changes
      //setSelectedRows(new Set());  //Copilot fix so selected rows stay selected when scrolling the grid.
      setMessagesDataState(e.dataState);
    },
    [setMessagesDataState]
  );

  const handleClearFilter = useCallback(() => {
    setMessagesDataState((prev: State) => ({
      ...prev,
      filter: { logic: 'and', filters: [] },
      sort: [{ field: 'creationDate', dir: 'desc' }],
      skip: 0,
      take: prev.take || 50,
    }));
    setDeletedFilter({ value: 'not deleted', label: t('Show All Active') });
  }, [setMessagesDataState, t]);

  // Reset pagination when toolbar filter changes
  useEffect(() => {
    setMessagesDataState((prev: State) => ({
      ...prev,
      skip: 0,
    }));
  }, [deletedFilter, setMessagesDataState]);

  const gridToolbarChildren = useMemo(
    () => (
      <ViewMessagesTableToolbar
        deletedFilter={deletedFilter}
        setDeletedFilter={setDeletedFilter}
        filteredMessages={processedData.data}
        clearFilter={handleClearFilter}
      />
    ),
    [deletedFilter, setDeletedFilter, processedData.data, handleClearFilter]
  );

  const onSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
    const { rowId } = event.dataItem;
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement = event?.syntheticEvent?.target as HTMLInputElement;
      const checked = checkboxElement?.checked ?? false;
      if (checked) {
        // Select all visible rows on current page only (not entire dataset)
        const allRowIds = new Set(
          processedData.data
            .map((row: Message) => row.rowId)
            .filter((id): id is string => id !== undefined)
        );
        setSelectedRows(allRowIds);
      } else {
        // Deselect all
        setSelectedRows(new Set());
      }
    },
    [processedData.data]
  );

  const cellClickFns = useCallback(
    (clickData: CellClickData) => {
      if (clickData.type === 'subject-inbox') {
        setSelectedInboxMessage(clickData.data);
        setIsInboxModalOpen(true);
      } else if (clickData.type === 'subject-outbox') {
        setSelectedMessageForModal(clickData.data);
        setIsMessageDetailsModalOpen(true);
      } else if (clickData.type === 'subject') {
        // Fallback for generic subject click - open outbox modal
        setSelectedMessageForModal(clickData.data);
        setIsMessageDetailsModalOpen(true);
      } else {
        // Handle other click types (existing functionality)
        setLatestClickedId(clickData.data?.messageId || 0);
      }
      return {
        latestClickedId,
        setLatestClickedId,
        refreshList: refreshTable,
      };
    },
    [refreshTable, latestClickedId]
  );

  return (
    <main className="message-layout main-container">
      <div className="flex justify-center flex-wrap gap-3 mx-4 my-2">
        {hasValidIdentity && (isOutboxMode || isInboxMode) && (
          <Button
            color="primary"
            onClick={() => setIsComposeModalOpen(true)}
            aria-label={t('Compose new message')}
          >
            {t('Compose Message')}
          </Button>
        )}
        <DeleteMessage
          clientId={clientId ?? undefined}
          selectedDel={selectedToDelete}
          selectedUndel={selectedToUndelete}
          origin={isInboxMode ? MESSAGE_MODE.INBOX : MESSAGE_MODE.OUTBOX}
        />
        <UnDeleteMessage
          clientId={clientId ?? undefined}
          selectedDel={selectedToDelete}
          selectedUndel={selectedToUndelete}
          origin={isInboxMode ? MESSAGE_MODE.INBOX : MESSAGE_MODE.OUTBOX}
        />
      </div>

      <div
        style={{ width: '100%', margin: '0 auto', overflow: 'auto' }}
        className="outbox-grid-container"
      >
        <GridWrapper
          id="viewMessageTable"
          dataSource={processedData.data}
          total={processedData.total}
          dataState={messagesDataState}
          columns={viewMessageColumns}
          height={`${height - 200}px`}
          scrollable="scrollable"
          gridToolbarChildren={gridToolbarChildren}
          cellClick={cellClickFns}
          onDataStateChange={changeDataState}
          {...defaultProps}
          loading={isMessageLoading}
          onHeaderSelectionChange={onHeaderSelectionChange}
          onSelectionChange={onSelectionChange}
          noRecordText={t('No records to display')}
          disableAutoResize={false}
          disableCellTooltip={false}
        />
      </div>

      <MessageDetailsOutboxModal
        visible={isMessageDetailsModalOpen}
        closeModal={() => {
          setIsMessageDetailsModalOpen(false);
          setSelectedMessageForModal(null);
        }}
        message={selectedMessageForModal}
      />

      <MessageDetailsInboxModal
        visible={isInboxModalOpen}
        closeModal={() => {
          setIsInboxModalOpen(false);
          setSelectedInboxMessage(null);
          // Refresh the grid to reflect any status changes (e.g., New -> Read)
          refreshTable();
        }}
        message={selectedInboxMessage}
      />

      <ComposeMessage
        visible={isComposeModalOpen}
        closeModal={() => setIsComposeModalOpen(false)}
      />
    </main>
  );
};

// Wrap component in memo to prevent unnecessary re-renders
export default memo(ViewMessageTable);
